<template>
    <div
        class="ppv2-flex lg:ppv2-flex-row ppv2-flex-col ppv2-justify-between ppv2-items-center position-relative ppv2-z-10 ppv2-ml-auto"
    >
        <OnboardingBanner
            class="d-none d-md-block w-100"
            v-if="showOnboarding && !isAllOnboardingCompleted"
            :current-step="currentOnboardingStep"
            :total-steps="onboardingSteps.length"
            :current-step-index="currentStepIndex + 1"
            :is-visible="showOnboarding"
            :action-url="onboardingActionUrl"
            :action-label="onboardingActionLabel"
            @close="hideOnboarding"
        />
        <div
            class="ppv2-flex ppv2-justify-end ppv2-px-[1%] ppv2-items-center position-relative ppv2-z-10 ppv2-ml-auto"
            :style="{ gap: normalizeSize(16, true), paddingRight: normalizeSize(16, true), paddingLeft: normalizeSize(16, true) }"
        >
            <!-- Bouton dark/light (Hidden on mobile) -->
            <div class="d-none d-md-block">
                <Theme />
            </div>

            <!-- Bouton IA -->
            <Chatbot />

            <!-- Avatar + Menu Container -->
            <div class="ppv2-flex ppv2-items-center ppv2-gap-2" ref="menuContainer">
                <!-- Avatar -->
                <div
                    class="borderOpacity md:!ppv2-border-[#e2e8f066] ppv2-border-black ppv2-border-opacity-40 d-flex align-items-center justify-content-center ppv2-h-[32px] ppv2-w-[32px] ppv2-border-[0.78px] ppv2-rounded-full cursor-pointer"
                    @click.stop="toggleMenu"
                >
                    <div class="md:!ppv2-text-[#fff] ppv2-text-black ppv2-text-xs">{{ initials }}</div>
                </div>

                <!-- Chevron -->
                <div class="cursor-pointer d-none d-md-block" @click.stop="toggleMenu">
                    <ChevronDown :size="15" color="white" v-if="!showMenu" />
                    <ChevronUp :size="15" color="white" v-if="showMenu" />
                </div>
            </div>

            <!-- Dropdown Menu -->
            <transition name="fade">
                <div v-if="showMenu" class="dropdownMenu" ref="dropdownMenu">
                    <!-- En-tête du dropdown -->
                    <div class="ppv2-px-4 ppv2-py-3 ppv2-border-b ppv2-border-[#333536] !ppv2-pt-[35px]">
                        <X
                            @click.stop="toggleMenu"
                            color="#94A3B8"
                            class="position-absolute ppv2-right-3 ppv2-top-3 cursor-pointer"
                        />
                        <div class="d-flex flex-column ppv2-items-center ppv2-gap-3">
                            <div class="avatar-container">
                                <div class="text-capitalize ppv2-text-black ppv2-text-sm poppinsRegular" v-if="user">
                                    {{ initials }}
                                </div>
                            </div>
                            <div class="text-capitalize text-center d-flex flex-column gap-1">
                                <div class="ppv2-text-black ppv2-text-sm poppinsRegular">
                                    {{ user.first_name }} {{ user.last_name }}
                                </div>
                                <div v-if="roles.length" class="text-capitalize ppv2-text-black ppv2-text-xs poppinsLight">
                                    {{ roles[0] || '' }}
                                </div>
                            </div>
                        </div>
                        <!-- Gérer mon compte -->
                        <div class="btnCompte custom-second-item ppv2-text-black" @click="requestAction('account')">
                            <span>{{ $t('navbar.manage_account') }}</span>
                        </div>
                        <!-- Section Premium -->
                        <div class="ppv2-flex ppv2-items-center ppv2-justify-between badge-primary">
                            <span class="ppv2-text-xs gap-2">
                                <Star :size="12" color="white" /> {{ planName || $t('subscription.plans.premium.name') }}
                                <template v-if="trialEndsIn > 0">-{{ $t('subscription.manage.trial') }}</template>
                            </span>
                        </div>
                        <div class="ppv2-flex ppv2-items-center ppv2-justify-center">
                            <span v-if="trialEndsIn > 0" class="ppv2-text-xs ppv2-text-black ppv2-text-[11px]">
                                {{ trialEndsIn }} {{ $tc('navbar.days_remaining', trialEndsIn) }}
                            </span>
                        </div>
                    </div>

                    <!-- Items du menu -->
                    <div class="ppv2-py-1">
                        <div v-if="hasFeatureChangeAccount" class="ppv2-border-b ppv2-border-[#333536] p-3 mb-0">
                            <!-- Changer de compte -->
                            <div
                                class="!ppv2-pl-[16px] ppv2-text-black poppinsMedium custom-item"
                                @click.stop="toggleAccountMenu"
                            >
                                <ArrowRightLeft :size="16" />
                                <span>{{ $t('navbar.change_account') }}</span>
                                <ChevronRight :size="14" class="ppv2-ml-auto" :class="{ 'ppv2-rotate-90': showAccountMenu }" />
                            </div>

                            <!-- Sous-menu des comptes -->
                            <transition name="slide">
                                <div
                                    v-if="showAccountMenu"
                                    class="ppv2-ml-4 ppv2-mr-4 ppv2-mt-1 ppv2-mb-1 ppv2-space-y-1 ppv2-rounded ppv2-p-2"
                                >
                                    <div class="custom-item" @click="switchAccount('bordeaux')">
                                        <span>Face à Face Bordeaux</span>
                                    </div>
                                    <div class="custom-item" @click="switchAccount('tours')">
                                        <span>Face à Face Tours</span>
                                    </div>
                                    <div class="custom-item" @click="switchAccount('colmar')">
                                        <span>Face à Face Colmar</span>
                                    </div>
                                </div>
                            </transition>
                        </div>
                        <!-- Items sans checkboxes -->
                        <div class="px-3 ppv2-space-y-0 ppv2-pt-[18px] ppv2-pb-[14px]">
                            <!-- Theme toggle visible only on mobile -->
                            <div v-if="isMobile" class="custom-second-item ppv2-text-black" @click="toggleTheme">
                                <MoonStar v-if="!isDark" :size="16" />
                                <Sun v-else :size="16" />
                                <span>{{ isDark ? $t('navbar.light_theme') : $t('navbar.dark_theme') }}</span>
                            </div>
                            <div
                                v-if="hasFeatureSubscription"
                                class="custom-second-item ppv2-text-black"
                                @click="requestAction('subscription')"
                            >
                                <RefreshCw :size="16" />
                                <span>{{ $t('navbar.subscription_billing') }}</span>
                            </div>
                            <div class="custom-second-item ppv2-text-black" @click="handleMenuAction('news')">
                                <NotepadText :size="18" />
                                <span>{{ $t('navbar.product_news') }}</span>
                            </div>
                            <div class="custom-second-item ppv2-text-black" @click="handleMenuAction('help')">
                                <HelpCircle :size="19" />
                                <span>{{ $t('navbar.help_center') }}</span>
                            </div>
                            <div
                                v-if="hasFeatureFormations"
                                class="custom-second-item ppv2-text-black"
                                @click="handleMenuAction('training')"
                            >
                                <GraduationCap :size="18" />
                                <span>{{ $t('navbar.training_solutions') }}</span>
                            </div>
                            <div
                                v-if="hasFeatureParrinage"
                                class="custom-second-item ppv2-text-black"
                                @click="handleMenuAction('referral')"
                            >
                                <Share2 :size="16" />
                                <span>{{ $t('navbar.referral') }}</span>
                                <span class="parrinageAmount ppv2-text-black ppv2-text-xs ppv2-px-2 ppv2-py-1 ppv2-rounded-full">
                                    200{{ $symbolCurrency }} {{ $t('navbar.offered') }}
                                </span>
                            </div>
                        </div>

                        <!-- Séparateur -->
                        <div class="ppv2-py-1">
                            <div class="ppv2-border-t ppv2-border-[#333536]"></div>
                        </div>

                        <!-- Déconnexion -->
                        <div
                            class="!ppv2-pl-[30px] custom-second-item !ppv2-pt-[16px] !ppv2-pb-[20px] ppv2-text-black ppv2-opacity-60"
                            @click="requestAction('logout')"
                        >
                            <LogOut :size="16" />
                            <span>{{ $t('navbar.logout') }}</span>
                        </div>
                    </div>
                </div>
            </transition>
        </div>

        <!-- Confirm Dialog -->
        <CustomDialog v-model="showConfirmDialog" :defaultTitle="dialogTitle" noScroll>
            <template #content>
                <div class="ppv2-text-center ppv2-py-4">
                    <p class="ppv2-text-black ppv2-text-base">{{ $t('experience.descriptionClosePopup') }}</p>
                </div>
            </template>
            <template #actions>
                <div class="ppv2-flex ppv2-gap-3 ppv2-w-full ppv2-justify-center">
                    <BaseButton variant="outline" @click="confirmAction" customClass="!ppv2-min-w-[140px]">
                        {{ $t('experience.btnLeave') }}
                    </BaseButton>
                    <BaseButton variant="primary" @click="cancelAction" customClass="!ppv2-min-w-[140px]">
                        {{ $t('experience.btnStay') }}
                    </BaseButton>
                </div>
            </template>
        </CustomDialog>
    </div>
</template>

<script>
import normalizeSize from '../../../utils/normalizeSize'

import CustomDialog from '../../Shared/CustomDialog.vue'
import BaseButton from '../../Shared/BaseButton.vue'
import Search from './components/Search.vue'
import Theme from './components/Theme.vue'
import Chatbot from './components/Chatbot.vue'
import OnboardingBanner from '../../Shared/OnboardingBanner.vue'
import LanguageSwitcher from '../../TenantRegistration/Parts/LanguageSwitcher.vue'

import {
    ChevronDown,
    ChevronUp,
    ChevronRight,
    LogOut,
    ArrowRightLeft,
    RefreshCw,
    NotepadText,
    HelpCircle,
    GraduationCap,
    Share2,
    Star,
    X,
    MoonStar,
    Sun,
} from 'lucide-vue'
import { EventBus } from '../../../eventBus.js'
import { theme, setTheme } from '../../../utils/theme'
import axios from 'axios'
import i18n from '../../../i18n'

export default {
    name: 'NavBar',
    components: {
        CustomDialog,
        BaseButton,
        Search,
        Theme,
        Chatbot,
        OnboardingBanner,
        LanguageSwitcher,
        ChevronDown,
        ChevronUp,
        ChevronRight,
        LogOut,
        ArrowRightLeft,
        RefreshCw,
        NotepadText,
        HelpCircle,
        GraduationCap,
        Share2,
        Star,
        X,
        MoonStar,
        Sun,
    },
    props: {
        hasFeatureChangeAccount: {
            type: Boolean,
            default: false, //Masqué actuellement ==> pas encore developper
        },
        hasFeatureParrinage: {
            type: Boolean,
            default: false, //Masqué actuellement ==> pas encore developper
        },
        hasFeatureFormations: {
            type: Boolean,
            default: false, //Masqué actuellement ==> pas encore developper
        },
        hasFeatureSubscription: {
            type: Boolean,
            default: true,
        },
        isSubscribed: {
            type: Boolean,
            default: false,
        },
        trialEndsIn: {
            type: Number,
            default: -1,
        },
        planName: {
            type: String,
            default: '',
        },
        user: {
            type: Object,
            default: null,
        },
        roles: {
            type: Array,
            default: () => [],
        },
        hasPublishedExperiences: {
            type: Boolean,
            default: false,
        },
        establishmentSettingsStatus: {
            type: Object,
            default: () => ({
                has_name: false,
                has_language: false,
                has_company_name: false,
                has_timezone: false,
                has_registration_number: false,
                has_address: false,
                has_hours: false,
                is_complete: false,
            }),
        },
        establishmentId: {
            type: [String, Number],
            default: 1,
        },
    },
    data() {
        return {
            windowWidth: window.innerWidth,
            localEstablishmentStatus: {
                has_name: this.establishmentSettingsStatus?.has_name || false,
                has_language: this.establishmentSettingsStatus?.has_language || false,
                has_company_name: this.establishmentSettingsStatus?.has_company_name || false,
                has_timezone: this.establishmentSettingsStatus?.has_timezone || false,
                has_registration_number: this.establishmentSettingsStatus?.has_registration_number || false,
                has_address: this.establishmentSettingsStatus?.has_address || false,
                has_hours: this.establishmentSettingsStatus?.has_hours || false,
                has_published_experiences: this.hasPublishedExperiences,
                has_stripe: this.establishmentSettingsStatus?.has_stripe || false,
                is_complete: this.establishmentSettingsStatus?.is_complete || false,
            },
            showMenu: false,
            showAccountMenu: false,
            showOnboarding: true,
            showConfirmDialog: false,
            pendingAction: null,
        }
    },
    computed: {
        isMobile() {
            return this.windowWidth <= 767
        },
        initials() {
            if (!this.user) return ''
            const first = this.user.first_name?.charAt(0).toUpperCase() || ''
            const last = this.user.last_name?.charAt(0).toUpperCase() || ''
            return `${first}${last}`
        },
        isDark() {
            return theme.mode === 'dark'
        },
        dialogTitle() {
            return this.$t('experience.titleClosePopup')
        },
        isAllOnboardingCompleted() {
            return (
                this.localEstablishmentStatus.is_complete &&
                this.localEstablishmentStatus.has_published_experiences &&
                this.localEstablishmentStatus.has_stripe
            )
        },
        onboardingSteps() {
            return [
                {
                    id: 1,
                    title: this.$t('common.onboarding_banner.steps.parameters.title'),
                    items: [
                        {
                            label: this.$t('common.onboarding_banner.steps.parameters.name'),
                            url: `/back-office/v2/establishment/${this.establishmentId}/edit#info:general`,
                            completed: this.localEstablishmentStatus.has_name,
                        },
                        {
                            label: this.$t('common.onboarding_banner.steps.parameters.company_name'),
                            url: `/back-office/v2/establishment/${this.establishmentId}/edit#info:general`,
                            completed: this.localEstablishmentStatus.has_company_name,
                        },
                        {
                            label: this.$t('common.onboarding_banner.steps.parameters.language'),
                            url: `/back-office/v2/establishment/${this.establishmentId}/edit#info:general`,
                            completed: this.localEstablishmentStatus.has_language,
                        },
                        {
                            label: this.$t('common.onboarding_banner.steps.parameters.timezone'),
                            url: `/back-office/v2/establishment/${this.establishmentId}/edit#info:general`,
                            completed: this.localEstablishmentStatus.has_timezone,
                        },
                        {
                            label: this.$t('common.onboarding_banner.steps.parameters.registration_number'),
                            url: `/back-office/v2/establishment/${this.establishmentId}/edit#info:general`,
                            completed: this.localEstablishmentStatus.has_registration_number,
                        },
                        {
                            label: this.$t('common.onboarding_banner.steps.parameters.address'),
                            url: `/back-office/v2/establishment/${this.establishmentId}/edit#info:location`,
                            completed: this.localEstablishmentStatus.has_address,
                        },
                        {
                            label: this.$t('common.onboarding_banner.steps.parameters.hours'),
                            url: `/back-office/v2/establishment/${this.establishmentId}/edit#schedule`,
                            completed: this.localEstablishmentStatus.has_hours,
                        },
                    ],
                    completed: this.localEstablishmentStatus.is_complete,
                },
                {
                    id: 2,
                    title: this.$t('common.onboarding_banner.steps.experience.title'),
                    items: [],
                    completed: this.localEstablishmentStatus.has_published_experiences,
                },
                {
                    id: 3,
                    title: this.$t('common.onboarding_banner.steps.stripe.title'),
                    items: [],
                    completed: this.localEstablishmentStatus.has_stripe,
                },
            ]
        },
        currentStepIndex() {
            const index = this.onboardingSteps.findIndex((step) => !step.completed)
            return index !== -1 ? index : 0
        },
        currentOnboardingStep() {
            return this.onboardingSteps[this.currentStepIndex]
        },
        onboardingActionUrl() {
            if (this.currentOnboardingStep?.id === 2) {
                return '/back-office/v3/experience/'
            }
            if (this.currentOnboardingStep?.id === 3) {
                return `/back-office/v2/establishment/${this.establishmentId}/edit#billing:stripeSetup`
            }
            return `/back-office/v2/establishment/${this.establishmentId}/edit`
        },
        onboardingActionLabel() {
            if (this.currentOnboardingStep?.id === 2) {
                return this.$t('common.onboarding_banner.create_now')
            }
            return this.$t('common.onboarding_banner.configure_now')
        },
    },
    mounted() {
        document.addEventListener('click', this.handleClickOutside)
        EventBus.$on('establishment-settings-updated', this.fetchOnboardingStatus)
        // Add listener properly checking event
        this.resizeHandler = () => {
            this.windowWidth = window.innerWidth
        }
        window.addEventListener('resize', this.resizeHandler)
    },
    beforeDestroy() {
        document.removeEventListener('click', this.handleClickOutside)
        EventBus.$off('establishment-settings-updated', this.fetchOnboardingStatus)
        window.removeEventListener('resize', this.resizeHandler)
    },
    methods: {
        normalizeSize,
        handleClickOutside(event) {
            const menuContainer = this.$refs.menuContainer
            const dropdownMenu = this.$refs.dropdownMenu

            if (menuContainer && !menuContainer.contains(event.target) && dropdownMenu && !dropdownMenu.contains(event.target)) {
                this.showMenu = false
                this.showAccountMenu = false
            }
        },
        toggleAccountMenu() {
            this.showAccountMenu = !this.showAccountMenu
        },
        switchAccount(account) {
            console.log(`🔄 Changement vers le compte: ${account}`)
            this.showAccountMenu = false
            this.showMenu = false
        },
        openUrl(url, target = '_self') {
            if (!url) return
            window.open(url, target, 'noopener,noreferrer')
        },
        handleMenuAction(action) {
            this.showMenu = false
            let url = ''
            let target = '_self'
            const getUrl = window.location
            const baseUrl = getUrl.protocol + '//' + getUrl.host
            switch (action) {
                case 'subscription':
                    url = baseUrl + (this.isSubscribed ? '/back-office/manage-subscription' : '/back-office/subscription')
                    break
                case 'news':
                    url =
                        'https://balsam-payment-a6b.notion.site/2aaef4c47c1f8079838af01628cbab9a?v=2aaef4c47c1f810b8744000c4f06bb3a'
                    target = '_blank'
                    break
                case 'help':
                    url = baseUrl + '/back-office/helpdesk'
                    break
            }
            if (url) {
                this.openUrl(url, target)
            }
        },
        toggleMenu() {
            this.showMenu = !this.showMenu
            if (!this.showMenu) {
                this.showAccountMenu = false
            }
        },
        requestAction(type) {
            this.showMenu = false
            this.pendingAction = type

            const navEvent = { defaultPrevented: false }
            EventBus.$emit('request-navigation', navEvent)

            if (navEvent.defaultPrevented) {
                this.showConfirmDialog = true
            } else {
                EventBus.$emit('force-navigation')
                this.executeNavigation(type)
            }
        },
        executeNavigation(action) {
            const baseUrl = window.location.protocol + '//' + window.location.host
            switch (action) {
                case 'logout': {
                    localStorage.removeItem('user')
                    window.location.href = baseUrl + '/deconnexion/back-office'
                    break
                }
                case 'account':
                    window.location.href = baseUrl + '/back-office/v3/profil'
                    break
                case 'subscription': {
                    const subUrl = this.isSubscribed ? '/back-office/manage-subscription' : '/back-office/subscription'
                    window.location.href = baseUrl + subUrl
                    break
                }
            }
        },
        confirmAction() {
            this.showConfirmDialog = false
            const action = this.pendingAction
            this.pendingAction = null

            EventBus.$emit('force-navigation')
            this.executeNavigation(action)
        },
        cancelAction() {
            this.showConfirmDialog = false
            this.pendingAction = null
        },
        toggleTheme() {
            const newMode = this.isDark ? 'light' : 'dark'
            setTheme(newMode)
            if (this.$vuetify) {
                this.$vuetify.theme.dark = newMode === 'dark'
            }
        },
        hideOnboarding() {
            this.showOnboarding = false
        },
        async fetchOnboardingStatus() {
            try {
                const response = await axios.get('/back-office/v3/onboarding/onboarding-status', {
                    params: { establishment_id: this.establishmentId },
                })

                if (response.data.success) {
                    this.localEstablishmentStatus = { ...this.localEstablishmentStatus, ...response.data.data }
                    console.log('✨ Onboarding status refreshed (V3):', response.data.data)
                }
            } catch (error) {
                console.error('❌ Error fetching onboarding status (V3):', error)
            }
        },
    },
}
</script>

<style scoped>
.borderOpacity {
    border: 0.58px solid;
}

.fade-enter-active,
.fade-leave-active {
    transition: all 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
    transform: translateY(-5px);
}

/* Animation pour le sous-menu */
.slide-enter-active,
.slide-leave-active {
    transition: all 0.2s ease;
}

.slide-enter-from,
.slide-leave-to {
    opacity: 0;
    transform: translateY(-5px);
    max-height: 0;
}

.slide-enter-to,
.slide-leave-from {
    opacity: 1;
    transform: translateY(0);
    max-height: 150px;
}

/* Rotation du chevron */
.ppv2-rotate-90 {
    transform: rotate(90deg);
    transition: transform 0.2s ease;
}

.parrinageAmount {
    box-shadow: inset 0 0 0 1000px rgba(0, 0, 0, 0.1);
}

.dark .parrinageAmount {
    box-shadow: inset 0 0 0 1000px rgba(255, 255, 255, 0.1);
}
</style>
